// Shared keyboard/focus behaviour for modal dialogs and drawers.
//
// - Escape closes the dialog.
// - On open, focus moves into the panel (first focusable, or the panel itself).
// - Tab / Shift+Tab cycle within the panel while it is open (focus trap).
// - On close/unmount, focus is restored to the element that was active before.
//
// Nested dialogs (e.g. a delete-confirm panel inside a manager modal) are
// supported: only the most recently opened panel reacts to Escape/Tab, so a
// single Escape press closes just the topmost layer.
import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Stack of currently open panels; the last entry is the topmost dialog.
const openPanels: HTMLElement[] = [];

function focusables(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/** Escape closes; focus moves into the panel on open and is trapped (Tab cycles) while open; restored on close. */
export function useDialogA11y(
  open: boolean,
  panelRef: RefObject<HTMLElement>,
  onClose: () => void,
): void {
  // Keep the latest onClose without re-running the effect (callers often pass
  // inline arrows; re-running would reset focus on every render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    openPanels.push(panel);

    // Move focus inside the panel on the next frame so the panel has painted.
    const raf = requestAnimationFrame(() => {
      if (panel.contains(document.activeElement)) return;
      const first = focusables(panel)[0];
      if (first) {
        first.focus();
      } else {
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
        panel.focus();
      }
    });

    const onKeyDown = (e: KeyboardEvent) => {
      // Only the topmost open dialog handles keyboard events.
      if (openPanels[openPanels.length - 1] !== panel) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (e.key === 'Tab') {
        const items = focusables(panel);
        if (items.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const inside = active ? panel.contains(active) : false;

        if (e.shiftKey) {
          if (!inside || active === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (!inside || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      const idx = openPanels.lastIndexOf(panel);
      if (idx >= 0) openPanels.splice(idx, 1);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open, panelRef]);
}

export default useDialogA11y;
